import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircleQuestion, Phone, Mail, CheckCircle2, RotateCcw, Trash2, BellRing, BellOff } from "lucide-react";
import { API, auth, ghostBtn, handleAuthError, fmtDate } from "./shared";
import { useConfirm } from "./useConfirm";
import { QuestionReplyBox } from "./QuestionReplyBox";

const AdminQuestions = ({ token, logout }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [notif, setNotif] = useState(null);
  const { confirm, dialog } = useConfirm();

  const load = () => axios.get(`${API}/questions`, auth(token)).then((r) => setItems(r.data)).catch((err) => handleAuthError(err, logout, () => {}));
  useEffect(() => { load(); axios.get(`${API}/notifications/status`, auth(token)).then((r) => setNotif(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (q, status) => {
    try { await axios.patch(`${API}/questions/${q.id}`, { status }, auth(token)); load(); }
    catch (err) { handleAuthError(err, logout, () => toast.error("Échec")); }
  };
  const remove = async (q) => {
    if (!(await confirm({ title: "Supprimer cette question ?", description: `La question de ${q.name} sera définitivement supprimée.` }))) return;
    try { await axios.delete(`${API}/questions/${q.id}`, auth(token)); load(); }
    catch (err) { handleAuthError(err, logout, () => toast.error("Échec")); }
  };

  const newCount = items.filter((q) => q.status === "new").length;
  const visible = filter === "all" ? items : items.filter((q) => q.status === filter);

  return (
    <div data-testid="admin-questions-tab">
      {dialog}
      {notif && (
        <div className="mb-8 grid gap-3 sm:grid-cols-2" data-testid="admin-questions-notif">
          <div className={`flex items-start gap-3 rounded-2xl p-5 ${notif.email ? "bg-mint" : "bg-cream"}`} data-testid="admin-questions-notif-email">
            {notif.email ? <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-brand" /> : <BellOff className="mt-0.5 h-5 w-5 shrink-0 text-gold" />}
            <div className="text-sm text-forest"><b>Alerte e-mail</b> : {notif.email && notif.admin_email ? <>activée, chaque nouvelle question est envoyée à <b>{notif.admin_email}</b></> : "non configurée"}.</div>
          </div>
          <div className={`flex items-start gap-3 rounded-2xl p-5 ${notif.twilio_whatsapp ? "bg-mint" : "bg-cream"}`} data-testid="admin-questions-notif-whatsapp">
            {notif.twilio_whatsapp ? <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-brand" /> : <BellOff className="mt-0.5 h-5 w-5 shrink-0 text-gold" />}
            <div className="text-sm text-forest"><b>Alerte WhatsApp</b> : {notif.twilio_whatsapp ? <>activée vers {notif.alert_numbers.join(", ")}</> : "en attente des clés Twilio (numéros prévus : " + notif.alert_numbers.join(", ") + ")"}.</div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {[{ id: "all", label: `Toutes (${items.length})` }, { id: "new", label: `À répondre (${newCount})` }, { id: "answered", label: `Répondues (${items.length - newCount})` }].map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`admin-questions-filter-${f.id}`} className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-all ${filter === f.id ? "border-gold bg-gold text-white" : "border-brand/20 bg-white text-brand hover:bg-mint"}`}>{f.label}</button>
        ))}
      </div>
      <div className="mt-6 space-y-4">
        {visible.map((q, i) => (
          <article key={q.id} data-testid={`admin-question-${i}`} className={`rounded-2xl border p-6 ${q.status === "new" ? "border-gold/40 bg-white shadow-md shadow-gold/5" : "border-brand/10 bg-mint/40"}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white ${q.status === "new" ? "bg-gold" : "bg-brand"}`} data-testid={`admin-question-${i}-status`}>{q.status === "new" ? "À répondre" : "Répondue"}</span>
                  <span className="font-mono text-[11px] uppercase tracking-widest text-brand">{fmtDate(q.created_at)}</span>
                </div>
                <h3 className="font-display mt-3 text-lg font-bold text-forest" data-testid={`admin-question-${i}-subject`}>{q.subject}</h3>
                <p className="text-sm font-semibold text-brand" data-testid={`admin-question-${i}-name`}>{q.name}</p>
                <div className="mt-1 flex flex-wrap gap-4 text-sm">
                  {q.phone && <a href={`tel:${q.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 text-brand hover:text-gold"><Phone className="h-3.5 w-3.5" />{q.phone}</a>}
                  {q.email && <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1.5 text-brand hover:text-gold"><Mail className="h-3.5 w-3.5" />{q.email}</a>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {q.status === "new"
                  ? <button onClick={() => setStatus(q, "answered")} data-testid={`admin-question-${i}-answered`} className={`${ghostBtn} border-brand text-brand`}><CheckCircle2 className="h-3.5 w-3.5" /> Répondue</button>
                  : <button onClick={() => setStatus(q, "new")} data-testid={`admin-question-${i}-reopen`} className={ghostBtn}><RotateCcw className="h-3.5 w-3.5" /> Rouvrir</button>}
                <button onClick={() => remove(q)} data-testid={`admin-question-${i}-delete`} className={`${ghostBtn} hover:border-gold hover:text-gold`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <p className="mt-5 whitespace-pre-line rounded-xl bg-white/70 p-4 text-sm leading-relaxed text-forest" data-testid={`admin-question-${i}-message`}>{q.message}</p>
            <QuestionReplyBox q={q} i={i} token={token} logout={logout} onDone={load} />
          </article>
        ))}
        {items.length === 0 && <p className="flex items-center gap-3 rounded-2xl border border-brand/10 bg-white px-5 py-8 text-sm text-brand"><MessageCircleQuestion className="h-5 w-5 text-gold" /> Aucune question reçue pour le moment.</p>}
      </div>
    </div>
  );
};

export default AdminQuestions;
